import { useEffect, useState } from "react";

import { Alert, Box, Button, Typography, useTheme, useMediaQuery } from "@mui/material";

import AddServiceDialog from "../../dialogs/addServiceDialog";
import EditServiceDialog from "../../dialogs/editServiceDialog";
import RemoveServiceDialog from "../../dialogs/removeServiceDialog";
import LoadingComponent from "../../shared_modules/loadingComponent";

import { useApi } from "../../hooks/useApi";

const Services = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();

  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showAddServiceDialog, setShowAddServiceDialog] = useState(false);
  const [showRemoveServiceDialog, setShowRemoveServiceDialog] = useState({
    show: false,
    service: null,
  });
  const [showEditDialog, setShowEditDialog] = useState({
    show: false,
    service: null,
  });
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  const retrieveServices = async () => {
    setIsLoading(true);
    try {
      const response = await api("/tenants/services.php", { method: "GET" });
      setServices(response.data.services);
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    retrieveServices();
  }, []);

  const active = services.filter((s) => s.is_active === 1);
  const inactive = services.filter((s) => s.is_active !== 1);

  const ServiceRow = ({ service }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        px: 3,
        py: 2,
        borderBottom: "0.1px solid",
        borderColor: "customDivider",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      {/* Row 1: Name + price */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {service.name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {service.duration_minutes} mins
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>·</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            ${service.price.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      {/* Row 2: Description + actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12, flex: 1, maxWidth: "55%", mr: 1 }}>
          {service.description || ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <Button
            size="small"
            onClick={() => setShowEditDialog({ show: true, service })}
            sx={{ textTransform: "none", fontSize: 12, color: "text.secondary", minWidth: 0, px: 1 }}
          >
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => setShowRemoveServiceDialog({ show: true, service })}
            sx={{ textTransform: "none", fontSize: 12, minWidth: 0, px: 1 }}
          >
            Remove
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <EditServiceDialog
        showEditDialog={showEditDialog.show}
        setShowEditDialog={setShowEditDialog}
        selectedService={showEditDialog.service}
        onSave={retrieveServices}
        showServiceAlert={showAlert}
        setServiceShowAlert={setShowAlert}
        type={"tenants"}
        selectedBarber={null}
      />

      <AddServiceDialog
        showAddServiceDialog={showAddServiceDialog}
        setShowAddServiceDialog={setShowAddServiceDialog}
        onSave={retrieveServices}
        showServiceAlert={showAlert}
        setServiceShowAlert={setShowAlert}
      />

      <RemoveServiceDialog
        showRemoveServiceDialog={showRemoveServiceDialog.show}
        setShowRemoveServiceDialog={setShowRemoveServiceDialog}
        selectedService={showRemoveServiceDialog.service}
        onSave={retrieveServices}
        showServiceAlert={showAlert}
        setServiceShowAlert={setShowAlert}
      />

      <Box
        sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, pt: 5, pb: 10 }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Services
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Manage the menu of cuts and treatments your shop offers.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={isLoading}
              onClick={() => setShowAddServiceDialog(true)}
            >
              Add service
            </Button>
          </Box>
        </Box>

        {showAlert.show && (
          <Alert
            sx={{ mb: 2 }}
            severity={showAlert.type}
            onClose={() => setShowAlert({ show: false, message: "", type: "" })}
          >
            {showAlert.message}
          </Alert>
        )}

        {isLoading ? (
          <LoadingComponent marginTop={10} />
        ) : services.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No services yet. Add your first service to start booking
            appointments.
          </Typography>
        ) : (
          <>
            {/* Active services */}
            {active.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", display: "block", mb: 1 }}
                >
                  Active ({active.length})
                </Typography>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: "0.1px solid",
                    borderColor: "customDivider",
                    backgroundColor: "offWhite",
                    overflow: "hidden",
                  }}
                >
                  {active.map((s) => (
                    <ServiceRow key={s.id} service={s} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Inactive services */}
            {inactive.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", display: "block", mb: 1 }}
                >
                  Inactive ({inactive.length})
                </Typography>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: "0.1px solid",
                    borderColor: "customDivider",
                    backgroundColor: "offWhite",
                    overflow: "hidden",
                    opacity: 0.7,
                  }}
                >
                  {inactive.map((s) => (
                    <ServiceRow key={s.id} service={s} />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default Services;
